import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 待機者募集情報ページ API 操作クラス
 */
export default class WaiterRecruitments {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET WaiterRecruitment
   */
  public async get(userStore: LoginUser): Promise<WaiterRecruitmentsGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterRecruitments, formatParams);

    const response = await this.commonService.executeGet<WaiterRecruitmentsGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET WaiterRecruitmentUrl
   */
  public async geWaiterRecruitmentUrl(userStore: LoginUser, queryParams?: { targetFacilityId?: number }): Promise<string> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterRecruitmentUrl, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<{ url: string }>(userStore, `${requestUrl}`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.url;
  }

  /**
   * ### POST WaiterRecruitment
   */
  public async post(userStore: LoginUser, requestBody: WaiterRecruitmentsPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterRecruitments, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH WaiterRecruitment
   */
  public async patch(userStore: LoginUser, requestBody: WaiterRecruitmentsPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.WaiterRecruitments, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface WaiterRecruitmentsGetModel {
  /** 施設ID */
  facilityId: number;

  /** 基本情報 */
  basicInformation: BasicInformation;
  /** 空室情報 */
  vacancyInformation: string;
  /** 施設見学 */
  visitReception: VisitReception;
  /** 掲載情報。配列で複数保持する（20 個まで）無い場合でも空配列を保持する */
  articles: Array<Article>;
  /** 画像・動画情報。配列で複数保持する（10 個まで）無い場合でも空配列を保持する */
  media: Array<Media>;
  /** 料金シミュレーションタイプ。コード値 */
  usageFeesType: UsageFeesType;
  /** 料金シミュレーション用の情報。費用タイプに応じて内容は可変 */
  usageFees: UsageFeesTokuyo | UsageFeesOnlyPaymentRate | UsageFeesOnlyUpperAndLower | UsageFeesGroupHome | null;
  /** 料金シミュレーション説明文 */
  usageFeesInformation: string;
  /** 資料。配列で複数保持する（10 個まで）無い場合でも空配列を保持する */
  documents: Array<Document>;
  /** よくある質問。配列で複数保持する（20 個まで）無い場合でも空配列を保持する */
  faqs: Array<Faq>;
  /** お問い合わせ */
  contact: Contact;
  /** 各コンテンツの表示可否 */
  showSettings: ShowSetting;

  /** 更新日時 (YYYYMMDDhhmmssSSS) */
  updated: number;
  /** 作成日時 (YYYYMMDDhhmmssSSS) */
  created: number;
}

export interface BasicInformation {
  /** 施設名 */
  facilityName: string;
  /** ヘッダー画像 */
  headerImages: Array<HeaderImage>;
  /** 施設種別 */
  facilityType: FacilityType | null;
  /** 施設住所 */
  address: string;
  /** 施設HPのURL */
  facilityUrl: string;
  /** 運営法人 */
  corporationName: string;
  /** 電話番号 */
  tel: string;
  /** Twitter(現在はXだがプロパティ名は変更しない) */
  twitterUrl: string;
  /** Facebook */
  facebookUrl: string;
  /** Instagram */
  instagramUrl: string;
  /** 会社HPのURL */
  corporationUrl: string | undefined;
}

export interface HeaderImage {
  /** ファイル名 */
  name: string;
  /** S3 ファイル URL */
  url: string;
  /** ファイル種別文字列 */
  type: string;
}

export enum FacilityType {
  /** 不明 */
  UNKNOWN = -1,
  /** 特別養護老人ホーム */
  TOKUYO = 1,
  /** 介護老人保健施設 */
  KAIGO_HOKEN = 2,
  /** グループホーム */
  GROUP_HOME = 3,
  /** ケアハウス */
  CARE_HOUSE = 4,
  /** 介護付き有料老人ホーム */
  KAIGO_YUURYOU = 5,
  /** 住宅型有料老人ホーム */
  JYUUTAKU_YUURYOU = 6,
  /** サービス付き高齢者向け住宅 */
  SERVICE_JYUUTAKU = 7
}

export interface VisitReception {
  /** 説明文 */
  description: string;
}

export interface Article {
  /** タイトル */
  title: string;
  /** 本文 */
  content: string;
}

export interface Media {
  /** ファイル名 */
  name: string;
  /** S3 ファイル URL */
  url: string;
  /** ファイル種別文字列 */
  type: string;
  /** 説明文 */
  description: string;
}

export enum UsageFeesType {
  /** 特養タイプ */
  UNKNOWN = -1,
  /** 特養タイプ */
  TOKUYOU = 1,
  /** 自立・負担割合変動タイプ */
  ONLY_PAYMENTRATE = 2,
  /** 上限・下限タイプ */
  ONLY_UPPER_AND_LOWER = 3,
  /** グループホームタイプ */
  GROUP_HOME = 4
}

export interface UsageFeesTokuyo {
  /** 居住費 */
  livingCosts: LivingCosts;
  /** 食費 */
  foodCosts: PaymentCeiling;
  /** 介護保険給付サービス利用料 */
  careInsuranceServiceCosts: CareInsuranceServiceCosts;
  /** 介護保険加算 */
  careInsuranceAddition: number | null;
  /** 実費費用 */
  actualCost: number | null;
}

export interface LivingCosts {
  /** 従来型多床室の部屋コスト情報 */
  multiBed: PaymentCeiling;
  /** 従来型個室の部屋コスト情報 */
  conventinalSingle: PaymentCeiling;
  /** ユニット型個室の部屋コスト情報 */
  unitTypeSingle: PaymentCeiling;
  /** ２人部屋（夫婦部屋）の部屋コスト情報 */
  double: PaymentCeiling;
  /** 個室の部屋コスト情報 */
  single: PaymentCeiling;

  /** facilities.customRecruitmentRoomも料金シミュレーションの対象なので保持する */
  customRecruitmentRoom: Array<ExtPaymentCeiling>;
}

export interface PaymentCeiling {
  /** 第1段階の値段 */
  paymentCeiling_1: number | null;
  /** 第2段階の値段 */
  paymentCeiling_2: number | null;
  /** 第3段階①の値段 */
  paymentCeiling_3_1: number | null;
  /** 第3段階②の値段 */
  paymentCeiling_3_2: number | null;
  /** 第4段階の値段 */
  paymentCeiling_4: number | null;
}

export interface ExtPaymentCeiling extends PaymentCeiling {
  /** id */
  id: string;
  /** 部屋名 */
  roomName: string;
}

export interface CareInsuranceServiceCosts {
  /** 従来型多床室の介護保険給付サービス利用料 */
  multiBed: CareLevel;
  /** 従来型個室の介護保険給付サービス利用料 */
  conventinalSingle: CareLevel;
  /** ユニット型個室の介護保険給付サービス利用料 */
  unitTypeSingle: CareLevel;
  /** ２人部屋（夫婦部屋）の介護保険給付サービス利用料 */
  double: CareLevel;
  /** 個室の介護保険給付サービス利用料 */
  single: CareLevel;

  /** facilities.customRecruitmentRoomも料金シミュレーションの対象なので保持する */
  customRecruitmentRoom: Array<ExtCareLevel>;
}

export interface CareLevel {
  /** 要介護1の値段 */
  careLevel_1: number | null;
  /** 要介護2の値段 */
  careLevel_2: number | null;
  /** 要介護3の値段 */
  careLevel_3: number | null;
  /** 要介護4の値段 */
  careLevel_4: number | null;
  /** 要介護5の値段 */
  careLevel_5: number | null;
}

export interface ExtCareLevel extends CareLevel {
  /** id */
  id: string;
  /** 部屋名 */
  roomName: string;
}

export interface UsageFeesOnlyPaymentRate {
  /** 従来型多床室の負担割合金額情報 */
  multiBed: NursingCare;
  /** 従来型個室の負担割合金額情報 */
  conventinalSingle: NursingCare;
  /** ユニット型個室の負担割合金額情報 */
  unitTypeSingle: NursingCare;
  /** ２人部屋（夫婦部屋）の負担割合金額情報 */
  double: NursingCare;
  /** 個室の負担割合金額情報 */
  single: NursingCare;

  /** facilities.customRecruitmentRoomも料金シミュレーションの対象なので保持する */
  customRecruitmentRoom: Array<ExtNursingCare>;
}

export interface NursingCare {
  /** 要介護認定有りの負担割合金額情報 */
  needNursingCare: BurdenRatio;
  /** 要介護認定無しの負担割合金額情報 */
  noNeedNursingCare: BurdenRatio;
}

export interface ExtNursingCare extends NursingCare {
  /** id */
  id: string;
  /** 部屋名 */
  roomName: string;
}

export interface BurdenRatio {
  /** 自立負担金額 */
  none: number | null;
  /** 1割負担負担金額 */
  percent_10: number | null;
  /** 2割負担負担金額 */
  percent_20: number | null;
  /** 3割負担負担金額 */
  percent_30: number | null;
}

export interface UsageFeesOnlyUpperAndLower {
  /** 下限料金 */
  minCosts: number | null;
  /** 上限料金 */
  maxCosts: number | null;
}

export interface UsageFeesGroupHome {
  /** 介護保険対象外 */
  nonCoveredAccommodationCosts: AccommodationCosts;
  /** 介護保険給付サービス利用料 */
  insuredServiceCosts: InsuredServiceCosts;
  /** 実費費用 */
  actualCost: number | null;
}

export interface AccommodationCosts {
  /** 従来型多床室 */
  multiBed: AccommodationCost;
  /** 従来型個室 */
  conventinalSingle: AccommodationCost;
  /** ユニット型個室 */
  unitTypeSingle: AccommodationCost;
  /** ２人部屋（夫婦部屋） */
  double: AccommodationCost;
  /** 個室 */
  single: AccommodationCost;

  /** カスタム募集部屋 */
  customRecruitmentRoom: Array<ExtAccommodationCost>;
}

export interface AccommodationCost {
  /** 居住費 */
  rentCost: number | null;
  /** 食費 */
  mealCost: number | null;
  /** 管理費 */
  managementCost: number | null;
  /** 光熱水道費 */
  utilityCost: number | null;
  /** その他 */
  otherCost: number | null;
}

export interface ExtAccommodationCost extends AccommodationCost {
  /** id */
  id: string;
  /** 部屋名 */
  roomName: string;
}

export interface InsuredServiceCosts {
  /** 従来型多床室 */
  multiBed: InsuredServiceCost;
  /** 従来型個室 */
  conventinalSingle: InsuredServiceCost;
  /** ユニット型個室 */
  unitTypeSingle: InsuredServiceCost;
  /** ２人部屋（夫婦部屋） */
  double: InsuredServiceCost;
  /** 個室 */
  single: InsuredServiceCost;

  /** カスタム募集部屋 */
  customRecruitmentRoom: Array<ExtInsuredServiceCost>;
}

export interface InsuredServiceCost {
  /** 要支援 1 */
  supportLevel_1: number | null;
  /** 要支援 2 */
  supportLevel_2: number | null;
  /** 要介護 1 */
  careLevel_1: number | null;
  /** 要介護 2 */
  careLevel_2: number | null;
  /** 要介護 3 */
  careLevel_3: number | null;
  /** 要介護 4 */
  careLevel_4: number | null;
  /** 要介護 5 */
  careLevel_5: number | null;
}

export interface ExtInsuredServiceCost extends InsuredServiceCost {
  /** id */
  id: string;
  /** 部屋名 */
  roomName: string;
}

export interface Document {
  /** 画面表示用のlinkテキスト */
  linkText: string;
  /** ファイル名 */
  name: string;
  /** S3ファイルURL */
  url: string;
}

export interface Faq {
  /** 質問 */
  question: string;
  /** 回答 */
  answer: string;
}

export interface Contact {
  /** 説明文 */
  description: string;
  /** 電話番号 */
  tel: string;
}

export interface ShowSetting {
  /** 施設見学の表示可否 */
  visitReception: boolean;
  /** 掲載情報の表示可否 */
  articles: boolean;
  /** 画像・動画情報の表示可否 */
  media: boolean;
  /** フォロワー募集の表示可否 */
  follower: boolean;
  /** 空室情報の表示可否 */
  vacancyInformation: boolean;
  /** 料金シミュレーションの表示可否 */
  usageFees: boolean;
  /** 資料の表示可否 */
  documents: boolean;
  /** よくある質問の表示可否 */
  faqs: boolean;
  /** お問い合わせの表示可否 */
  contact: boolean;
}

export interface WaiterRecruitmentsPostModel {
  /** 基本情報 */
  basicInformation: BasicInformation;
  /** 空室情報 */
  vacancyInformation: string;
  /** 施設見学 */
  visitReception: VisitReception;
  /** 掲載情報。配列で複数保持する（20 個まで）無い場合でも空配列を保持する */
  articles: Array<Article>;
  /** 画像・動画情報。配列で複数保持する（10 個まで）無い場合でも空配列を保持する */
  media: Array<Media>;
  /** 料金シミュレーションタイプ。コード値 */
  usageFeesType: UsageFeesType;
  /** 料金シミュレーション用の情報。費用タイプに応じて内容は可変 */
  usageFees: UsageFeesTokuyo | UsageFeesOnlyPaymentRate | UsageFeesOnlyUpperAndLower | UsageFeesGroupHome;
  /** 料金シミュレーション説明文 */
  usageFeesInformation: string | null;
  /** 資料。配列で複数保持する（10 個まで）無い場合でも空配列を保持する */
  documents: Array<Document>;
  /** よくある質問。配列で複数保持する（20 個まで）無い場合でも空配列を保持する */
  faqs: Array<Faq>;
  /** お問い合わせ */
  contact: Contact;
  /** 各コンテンツの表示可否 */
  showSettings: ShowSetting;
}

export interface WaiterRecruitmentsPatchModel {
  /** 基本情報 */
  basicInformation: BasicInformation;
  /** 空室情報 */
  vacancyInformation: string;
  /** 施設見学 */
  visitReception: VisitReception;
  /** 掲載情報。配列で複数保持する（20 個まで）無い場合でも空配列を保持する */
  articles: Array<Article>;
  /** 画像・動画情報。配列で複数保持する（10 個まで）無い場合でも空配列を保持する */
  media: Array<Media>;
  /** 料金シミュレーションタイプ。コード値 */
  usageFeesType: UsageFeesType;
  /** 料金シミュレーション用の情報。費用タイプに応じて内容は可変 */
  usageFees: UsageFeesTokuyo | UsageFeesOnlyPaymentRate | UsageFeesOnlyUpperAndLower | UsageFeesGroupHome;
  /** 料金シミュレーション説明文 */
  usageFeesInformation: string | null;
  /** 資料。配列で複数保持する（10 個まで）無い場合でも空配列を保持する */
  documents: Array<Document>;
  /** よくある質問。配列で複数保持する（20 個まで）無い場合でも空配列を保持する */
  faqs: Array<Faq>;
  /** お問い合わせ */
  contact: Contact;
  /** 各コンテンツの表示可否 */
  showSettings: ShowSetting;

  /** 更新日時 (YYYYMMDDhhmmssSSS) */
  updated: number;
}
